import type { PlayerCharacter } from "../types/entity.js";
import type { SRDClass } from "../types/srd.js";

/**
 * Pure level-up handler for party-and-characters (C11).
 *
 * Takes a character plus its class record and returns a new character
 * advanced by one level, along with a summary describing what changed.
 * Callers (W09 — world-and-progression) are responsible for triggering
 * the ASI modal when `asiRequired` is true and for writing the new
 * character back via `PartyStore.updateMember`.
 *
 * Per party-and-characters §Leveling:
 *  - HP: fixed class-defined increment (`classData.hp_per_level`).
 *    No die roll, no CON-mod variance.
 *  - Proficiency bonus: recalculated at bkcrawl breakpoints
 *    (+2 at 1–4, +3 at 5–8, +4 at 9+).
 *  - Spell slots: full table from `classData.spellcasting.slots_per_level`.
 *  - ASI modal: required at levels 3, 6, 9 (bkcrawl-custom, not SRD 4/8).
 *
 * Class features and known-spell growth are data the caller layers on
 * afterwards — neither is wired in v1.
 */

export const MAX_LEVEL = 10;
export const ASI_LEVELS: readonly number[] = [3, 6, 9];

export interface LevelUpSummary {
  readonly previousLevel: number;
  readonly newLevel: number;
  readonly hpGained: number;
  readonly proficiencyBefore: number;
  readonly proficiencyAfter: number;
  /** Per-level count of slots added (positive deltas only). */
  readonly slotsAdded: Readonly<Record<number, number>>;
  /** True when the caller should interrupt with an ASI modal. */
  readonly asiRequired: boolean;
}

export interface LevelUpResult {
  readonly character: PlayerCharacter;
  readonly summary: LevelUpSummary;
  /** True when the level-up actually occurred; false at level cap. */
  readonly leveled: boolean;
}

/**
 * bkcrawl proficiency curve (levels 1–10 only; v1 caps at 10).
 *   +2 at 1–4, +3 at 5–8, +4 at 9+.
 */
export function proficiencyForLevel(level: number): number {
  if (level < 5) return 2;
  if (level < 9) return 3;
  return 4;
}

/**
 * Returns the caster's full slot table at `level`, converted from the
 * SRD's 5-element array form into a `Record<number, number>` keyed by
 * spell level. Non-casters (or missing data) yield `{}`.
 */
export function maxSpellSlotsAtLevel(classData: SRDClass, level: number): Record<number, number> {
  const casting = classData.spellcasting;
  if (!casting) return {};
  const idx = Math.max(0, Math.min(casting.slots_per_level.length - 1, level - 1));
  const row = casting.slots_per_level[idx];
  if (!row) return {};
  const out: Record<number, number> = {};
  for (let i = 0; i < row.length; i++) {
    const count = row[i];
    if (count !== undefined && count > 0) out[i + 1] = count;
  }
  return out;
}

/**
 * Advances a character by one level. Returns the original character
 * unchanged (with `leveled: false`) when the character is already at
 * MAX_LEVEL.
 */
export function levelUpCharacter(
  character: PlayerCharacter,
  classData: SRDClass,
): LevelUpResult {
  const previousLevel = character.level;
  if (previousLevel >= MAX_LEVEL) {
    return {
      character,
      leveled: false,
      summary: {
        previousLevel,
        newLevel: previousLevel,
        hpGained: 0,
        proficiencyBefore: character.proficiencyBonus,
        proficiencyAfter: character.proficiencyBonus,
        slotsAdded: {},
        asiRequired: false,
      },
    };
  }

  const newLevel = previousLevel + 1;
  const hpGained = classData.hp_per_level;
  const newMaxSlots = maxSpellSlotsAtLevel(classData, newLevel);
  const previousSlots = character.spellSlots;

  const slotsAdded: Record<number, number> = {};
  const nextSlots: Record<number, number> = { ...previousSlots };
  for (const key of Object.keys(newMaxSlots)) {
    const level = Number(key);
    const before = previousSlots[level] ?? 0;
    const cap = newMaxSlots[level] ?? 0;
    if (cap > before) {
      slotsAdded[level] = cap - before;
      nextSlots[level] = cap;
    }
  }

  const proficiencyAfter = proficiencyForLevel(newLevel);

  const nextCharacter: PlayerCharacter = {
    ...character,
    level: newLevel,
    proficiencyBonus: proficiencyAfter,
    hp: character.hp + hpGained,
    maxHp: character.maxHp + hpGained,
    spellSlots: nextSlots,
  };

  return {
    character: nextCharacter,
    leveled: true,
    summary: {
      previousLevel,
      newLevel,
      hpGained,
      proficiencyBefore: character.proficiencyBonus,
      proficiencyAfter,
      slotsAdded,
      asiRequired: ASI_LEVELS.includes(newLevel),
    },
  };
}
